import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import endpoints from 'constants/endpoints';
import { safePostCall } from 'helpers/firebaseSetup';
import { showAlert } from 'state/actions/site';

const DEFAULT_SUCCESS_MESSAGE = 'The invitation was sent successfully.';
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Try again later.';

// portal_invite_to_lms sends this status when the agent is already registered in the
// LMS — it's an ok:false (nothing was sent) that must not be shown as a failure
export const LMS_INVITE_EXISTING_STATUS = 'existing';

// zoho functions don't all answer with the standard { ok, message } json,
// so a non-json output is treated as a successful execution
function parseInviteOutput(rawOutput) {
  if (rawOutput == null || rawOutput === '') {
    return { ok: false, status: '', message: '' };
  }

  const parsed =
    typeof rawOutput === 'string' ? safeJsonParse(rawOutput) : rawOutput;

  if (parsed == null) {
    return { ok: true, status: '', message: '' };
  }

  return {
    ok: parsed.ok !== false,
    status: String(parsed.status ?? '').trim(),
    message: String(parsed.message ?? '').trim()
  };
}

// null means the output wasn't json at all
function safeJsonParse(rawOutput) {
  try {
    return JSON.parse(rawOutput);
  } catch {
    return null;
  }
}

// an already registered agent is an outcome, not a failure
function getAlertCode({ ok, status }) {
  if (status === LMS_INVITE_EXISTING_STATUS) return 'info';
  if (ok) return 'success';
  return 'error';
}

/**
 * sends the Zoho Learn (LMS) invitation email to the logged in agent
 * @returns {{ agentEmail: string, isSending: boolean, result: object | null, sendInvite: function }}
 */
export default function useLmsInvite() {
  const [isSending, setIsSending] = useState(false);
  // { ok, message } as reported by portal_invite_to_lms, null before the first attempt
  const [result, setResult] = useState(null);

  const agentEmail = useSelector(state => state.user.zohoData?.Email ?? '');
  const dispatch = useDispatch();

  async function sendInvite() {
    if (!agentEmail) {
      dispatch(
        showAlert({
          message:
            'We could not find your email address. Please contact your administrator.',
          code: 'error'
        })
      );
      return;
    }

    setIsSending(true);

    try {
      const response = await safePostCall(endpoints.awsProxy.safePost, {
        isZohoRequest: true,
        endpoint: endpoints.zoho.inviteToLms,
        agentEmail
      });

      if (!response) {
        throw new Error('The portal could not reach Zoho. Try again later.');
      }

      const { ok, status, message } = parseInviteOutput(
        response.data?.details?.output
      );

      // an ok:false carrying a message is an expected outcome (already registered
      // agent, invite not allowed); no message means zoho rejected the call
      if (!ok && !message) {
        console.error('[lms-invite] unexpected zoho response', response.data);
        throw new Error(
          response.data?.message || 'The invitation could not be sent.'
        );
      }

      const outcome = {
        ok,
        status,
        message: message || DEFAULT_SUCCESS_MESSAGE
      };

      setResult(outcome);
      dispatch(
        showAlert({ message: outcome.message, code: getAlertCode(outcome) })
      );
    } catch (error) {
      const errorMessage = error.message || DEFAULT_ERROR_MESSAGE;

      setResult({ ok: false, status: '', message: errorMessage });
      dispatch(showAlert({ message: errorMessage, code: 'error' }));
    } finally {
      setIsSending(false);
    }
  }

  return { agentEmail, isSending, result, sendInvite };
}
